import React, { useState, useEffect } from 'react';
import {
  Grid,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Card,
  CardContent,
} from '@mui/material';
import { useParams, useHistory } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Typography } from 'src/components/Typography';
import { CreateJobOfferModal } from './components/CreateJobOfferModal';
import {
  useGetJobOffers,
  useGetJobById,
  useGetJobApplications,
  useTerminateJob,
  useRemoveLanguages,
  useHandleJobApplication,
  useCreateJobOfferBatch,
} from 'src/api/hooks/useJob';

import { WorkerProfile } from 'src/DTO/WorkerProfile.type';
import { useStoreActions } from 'src/stores';
import { PopUpEnum } from 'src/stores/NotificationStore';
import { JOB_STATUS } from 'src/DTO/JobStatus.enum';
import { RemoveWorkersFromJobDialog } from './components/RemoveWorkersFromJobDialog';
import { CreateJobDialog } from 'src/pages/Job/components/CreateJobDialog';
import { EditModes } from './Job.types';
import { JOB_OFFER_STATUS } from 'src/DTO/JobOfferStatus.enum';
import { HashAttributes } from 'src/DTO/WorkerInterview.type';
import { useGetInterviewHash } from 'src/api/hooks/useInterview';
import { ApplicationModal } from './components/ApplicationModal';
import { BUTTON_COLOR, BUTTON_SIZE, BUTTON_VARIANT } from 'src/components/Button/types';
import { TYPOGRAPHY_COLORS, TYPOGRAPHY_WEIGHT } from 'src/components/Typography/types';
import { WorkForceViewContent } from './components/WorkForceViewContent';
import { getLanguage } from 'src/utils/getLanguage';
import { useLocalizedData } from 'src/utils/useLocalizeData';
import { LanguageRemoval } from 'src/components/LanguageRemoval';
import { LanguageKeys } from 'src/components/LanguageSelectorContext/types';
import { JobDetailPageParams } from './JobDetail.types';
import { JobDetails } from 'src/components/JobDetails/JobDetails';
import { JobApplicationAction } from 'src/DTO/Job.type';
import { ROUTES } from 'src/utils/constants.util';
import { useRedirect } from 'src/hooks/useRedirect';
import { useFeatureFlagEnabled } from 'src/hooks/useFeatureFlags';
import { FeatureFlagKey } from 'src/hooks/useFeatureFlags.types';

export const JobDetailPage: React.FC = () => {
  const history = useHistory();
  const isFacebookFeatureEnabled = useFeatureFlagEnabled(FeatureFlagKey.FACEBOOK_AI);

  const { jobId } = useParams<JobDetailPageParams>();
  const { t } = useTranslation();

  const { handleRedirect } = useRedirect();

  const [getJobOffers, { data: allJobOffers }] = useGetJobOffers();
  const jobOffers = allJobOffers ?? [];

  const [getJobApplications, { data: jobApplications }] = useGetJobApplications();
  const [getJobById, { data: job, loading }] = useGetJobById();
  const [selectedLanguages, setSelectedLanguages] = useState<Record<LanguageKeys, boolean>>({
    hu: false,
    en: false,
  });

  const [workersToOfferJob, setWorkersToOfferJob] = useState<WorkerProfile[]>([]);

  const [terminateJob, { loading: terminateLoading }] = useTerminateJob({
    onComplete: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.success,
        message: t('jobPage.jobHasBeenTerminated'),
      });

      history.push({
        pathname: '/job',
      });
    },
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('jobPage.jobTerminationFailed'),
      });
    },
  });

  const [handleJobApplication, { loading: handleApplicationLoading }] = useHandleJobApplication({
    onComplete: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.success,
        message: t('jobPage.removeWorkersFromJobDialog.handleJobApplicationSuccess'),
      });

      setIsApplicationModalOpen(false);
      getJobById({ variables: { jobId } });
      getJobApplications({ variables: { jobId } });
    },
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('jobPage.removeWorkersFromJobDialog.handleJobApplicationFailed'),
      });
    },
  });

  const [removeLanguage] = useRemoveLanguages({
    onComplete: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.success,
        message: t('jobPage.languageHasBeenRemoved'),
      });

      history.push({
        pathname: '/job',
      });
    },
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('jobPage.languageRemovalFailed'),
      });
    },
  });

  const [createJobOfferBatch] = useCreateJobOfferBatch({
    onComplete: () => {
      getJobOffers({ variables: { jobId } });
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.success,
        message: `${
          extendedJobData.status === JOB_STATUS.ACTIVE
            ? t('jobPage.createJobOfferModal.createJobOffer.success')
            : t('jobPage.createJobOfferModal.createJobOffer.inactiveJobSuccess')
        }`,
      });
    },
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('jobPage.createJobOfferModal.createJobOffer.error'),
      });
    },
  });

  // LOCAL State
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [terminateWokerDialogOpen, setTerminateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [hashAttributes, setHashAttributes] = useState<HashAttributes>();
  const [isApplicationModalOpen, setIsApplicationModalOpen] = useState<boolean>(false);
  const [isLanguageRemovalDialogOpen, setIsLanguageRemovalDialogOpen] = useState<boolean>(false);
  const [applicationId, setSelectedApplicationId] = useState<string>('');
  const [selectedWorker, setSelectedWorker] = useState<WorkerProfile | undefined>();

  // GLOBAL State
  const openConfirmModal = useStoreActions(actions => actions.context.openConfirmDialog);
  const setPopUpData = useStoreActions(action => action.popUp.setPopUp);

  const [getInterviewHash] = useGetInterviewHash({
    onComplete: res => {
      if (res) {
        setHashAttributes(res);
      }
    },
  });

  useEffect(() => {
    getJobOffers({ variables: { jobId } });
    getJobApplications({ variables: { jobId } });
    getJobById({ variables: { jobId } });
    getInterviewHash();
  }, []);

  if (terminateLoading || loading || !job) {
    return (
      <Grid item xs={12}>
        <CircularProgress color="secondary" />
      </Grid>
    );
  }
  const { extendedJobData } = job;

  const { enJob, huJob } = useLocalizedData(extendedJobData);

  const isEngOnly = getLanguage(extendedJobData, 'en', 'hu');

  const offerJobToWorkers = () => {
    if (workersToOfferJob.length) {
      createJobOfferBatch({
        variables: {
          offers: workersToOfferJob.map(worker => ({
            workerId: worker.id,
            jobId: extendedJobData.id,
            name: worker.name,
            phoneNumber: worker.phoneNumber,
          })),
        },
      });
      setWorkersToOfferJob([]);
    }
  };

  const handleOpenWorkerProfile = (workerData: WorkerProfile) => {
    history.push({
      pathname: `/worker/${workerData.id}`,
      state: { worker: workerData },
    });
  };

  const handleTerminateJob = () => {
    openConfirmModal({
      title: t('jobPage.terminateJobAlertTitle'),
      body: (
        <Box>
          <Trans>jobPage.terminateJobAlertBody</Trans>
        </Box>
      ),
      onConfirm: () => {
        terminateJob({ variables: { id: extendedJobData.id } });
      },
    });
  };

  const handleLanguageRemoval = () => {
    setIsLanguageRemovalDialogOpen(true);
  };

  const handleConfirmLanguageRemoval = () => {
    setIsLanguageRemovalDialogOpen(false);
    removeLanguage({ variables: { jobId: extendedJobData.id, selectedLanguages } });
  };

  const handleOpenEditJob = () => {
    setIsEditDialogOpen(true);
  };

  const getEditMode = (): EditModes => {
    const documentReviewOffers = jobOffers.filter(
      offer => offer.status === JOB_OFFER_STATUS.UNDER_REVIEW,
    );
    const notDeclinedOffers = jobOffers.filter(offer => offer.status !== JOB_OFFER_STATUS.DECLINED);

    if (extendedJobData.workers.length > 0 || documentReviewOffers.length > 0) {
      return EditModes.ActiveJobState;
    }

    if (extendedJobData.workers.length === 0 && notDeclinedOffers.length > 0) {
      return EditModes.OfferState;
    }

    return EditModes.Full;
  };

  const handleDeclineApplication = (applicationId: string) => {
    handleJobApplication({ variables: { applicationId, action: JobApplicationAction.DECLINE } });
  };

  const handleAcceptApplication = (applicationId: string) => {
    handleJobApplication({ variables: { applicationId, action: JobApplicationAction.ACCEPT } });
  };

  const openApplicationModal = (worker: WorkerProfile, applicationId: string) => {
    setSelectedWorker(worker);
    setSelectedApplicationId(applicationId);
    setIsApplicationModalOpen(true);
  };

  const handleRedirectToFacebookAI = () => {
    if (!jobId) return;

    handleRedirect({
      route: ROUTES.FACEBOOK_AI,
      extraState: { redirectedFromJob: true, jobId },
    });
  };

  return (
    <Box>
      <Grid item xs={12}>
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}>
              {!isEngOnly
                ? extendedJobData.role.name
                : extendedJobData.role.localizedData?.en?.name}
            </Typography>
            <Box display="flex" mt={2}>
              <Box mt={1} mr={1}>
                <Button
                  sx={{ width: '9rem', height: 45, fontSize: 12 }}
                  variant={BUTTON_VARIANT.CONTAINED}
                  color={BUTTON_COLOR.ERROR}
                  type="submit"
                  disabled={extendedJobData.status === JOB_STATUS.TERMINATED}
                  onClick={handleTerminateJob}>
                  <Trans>jobPage.terminateJob</Trans>
                </Button>
              </Box>
              {enJob && huJob && (
                <Box mt={1} mr={1}>
                  <Button
                    sx={{ width: '9rem', height: 45, fontSize: 12 }}
                    variant={BUTTON_VARIANT.GRADIENT}
                    color={BUTTON_COLOR.INFO}
                    type="submit"
                    onClick={handleLanguageRemoval}>
                    <Trans>jobPage.removeLanguage</Trans>
                  </Button>
                </Box>
              )}
              <Box mt={1} mr={1}>
                <Button
                  sx={{ width: '9rem', height: 45, fontSize: 12 }}
                  variant={BUTTON_VARIANT.GRADIENT}
                  color={BUTTON_COLOR.INFO}
                  type="submit"
                  disabled={getEditMode() === EditModes.ActiveJobState}
                  onClick={handleOpenEditJob}>
                  <Trans>jobPage.editJob</Trans>
                </Button>
              </Box>
              {isFacebookFeatureEnabled && (
                <Box mt={1} mr={1}>
                  <Button
                    sx={{ width: '11rem', height: 45, fontSize: 12 }}
                    variant={BUTTON_VARIANT.GRADIENT}
                    color={BUTTON_COLOR.INFO}
                    type="submit"
                    disabled={
                      job.extendedJobData.status !== JOB_STATUS.ACTIVE ||
                      job.extendedJobData.postSchedule !== null
                    }
                    onClick={handleRedirectToFacebookAI}>
                    <Trans>jobPage.generateFacebookPost</Trans>
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        {extendedJobData.status === JOB_STATUS.TERMINATED && (
          <Box mt={2} mb={2}>
            <Typography
              variant="h3"
              fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}
              color={TYPOGRAPHY_COLORS.ERROR}>
              <Trans>jobPage.terminatedJobInfo</Trans>
            </Typography>
          </Box>
        )}
        <WorkForceViewContent
          jobOffers={jobOffers}
          jobApplications={jobApplications}
          job={extendedJobData}
          openApplicationModal={openApplicationModal}
          setTerminateDialogOpen={setTerminateDialogOpen}
          setIsInviteModalOpen={setIsInviteModalOpen}
          handleOpenWorkerProfile={handleOpenWorkerProfile}
        />
      </Grid>
      <CreateJobOfferModal
        isOpen={isInviteModalOpen}
        onSubmit={offerJobToWorkers}
        setIsOpen={setIsInviteModalOpen}
        workersToOfferJob={workersToOfferJob}
        setWorkersToOfferJob={setWorkersToOfferJob}
        jobId={extendedJobData.id}
      />
      <RemoveWorkersFromJobDialog
        jobId={extendedJobData.id}
        isOpen={terminateWokerDialogOpen}
        selectableWorkers={[...extendedJobData.workers, ...jobOffers.map(offer => offer.worker)]}
        handleCloseDialog={() => {
          setTerminateDialogOpen(false);
        }}
      />
      <CreateJobDialog
        open={isEditDialogOpen}
        setOpen={setIsEditDialogOpen}
        editRoleData={extendedJobData.role}
        editJobData={extendedJobData}
        editMode={getEditMode()}
        onSuccessCallback={newJob => getJobById({ variables: { jobId: newJob.id } })}
      />
      {hashAttributes && selectedWorker && (
        <ApplicationModal
          isOpen={isApplicationModalOpen}
          handleClose={() => setIsApplicationModalOpen(false)}
          worker={selectedWorker}
          hashAttributes={hashAttributes}
          loading={handleApplicationLoading}
          handleAcceptApplication={handleAcceptApplication}
          handleDeclineApplication={handleDeclineApplication}
          applicationId={applicationId}
          handleOpenWorkerProfile={handleOpenWorkerProfile}
        />
      )}

      <Dialog
        open={isLanguageRemovalDialogOpen}
        fullWidth={true}
        maxWidth={'sm'}
        onClose={() => setIsLanguageRemovalDialogOpen(false)}>
        <DialogTitle>{t('jobPage.removeLanguageAlertTitle')}</DialogTitle>
        <DialogContent sx={{ height: 170 }}>
          <Box mb={2}>
            <Trans>jobPage.removeLanguageAlertBody</Trans>
          </Box>
          <LanguageRemoval
            selectedLanguages={selectedLanguages}
            setSelectedLanguages={setSelectedLanguages}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsLanguageRemovalDialogOpen(false)} size={BUTTON_SIZE.SMALL}>
            <Trans>generic.cancel</Trans>
          </Button>
          <Button
            disabled={!(selectedLanguages.en || selectedLanguages.hu)}
            onClick={handleConfirmLanguageRemoval}
            variant={BUTTON_VARIANT.GRADIENT}
            size={BUTTON_SIZE.SMALL}
            color={BUTTON_COLOR.INFO}>
            <Trans>generic.save</Trans>
          </Button>
        </DialogActions>
      </Dialog>
      <Grid container>
        <Grid item xs={12} sx={{ mt: 5 }}>
          <Box mt={1} mb={2} display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}>
                <Trans>jobPage.jobDetails.title</Trans>
              </Typography>
            </Box>
          </Box>
          <Box>
            <Card>
              <CardContent>
                <JobDetails job={extendedJobData} />
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
